import { lazy, type ComponentType } from 'react';

const CHUNK_RECOVERY_KEY = 'incognito.chunk-recovery-at';
const CHUNK_RECOVERY_WINDOW_MS = 30_000;

export function isDynamicImportFailure(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /dynamically imported module|failed to fetch.*module|importing a module script failed|expected a javascript-or-wasm module script/i.test(message);
}

async function loadWithChunkRecovery<T>(load: () => Promise<T>): Promise<T> {
  try {
    const loaded = await load();
    try { window.sessionStorage.removeItem(CHUNK_RECOVERY_KEY); } catch { /* Storage may be unavailable in hardened browsers. */ }
    return loaded;
  } catch (error) {
    if (!isDynamicImportFailure(error)) throw error;

    const now = Date.now();
    let shouldReload = true;
    try {
      const previousAttempt = Number(window.sessionStorage.getItem(CHUNK_RECOVERY_KEY));
      shouldReload = !Number.isFinite(previousAttempt) || now - previousAttempt > CHUNK_RECOVERY_WINDOW_MS;
      if (shouldReload) window.sessionStorage.setItem(CHUNK_RECOVERY_KEY, String(now));
      else window.sessionStorage.removeItem(CHUNK_RECOVERY_KEY);
    } catch {
      // A reload remains the safest recovery when session storage is unavailable.
    }

    if (shouldReload) {
      window.location.reload();
      return new Promise<T>(() => undefined);
    }
    throw error;
  }
}

export function lazyRoute<T extends ComponentType<any>>(load: () => Promise<{ default: T }>) {
  return lazy(() => loadWithChunkRecovery(load));
}
